// 青波線（言い換え提案）
//
// 役割:
//   ユーザーが書いた文章を分析して、より自然・洗練された表現の言い換え候補を
//   青い波線でマークし、クリックでツールチップから候補を選んで置換できる UI を提供する。
//
// Phase 1（赤波線）との関係:
//   Phase 1 と並列で表示される。Phase 1 がルールベース校正、Phase 2 が LLM ベースの
//   コンテキスト依存提案。色を分けることで「赤波線の強化バージョン」として認識される。
//
// 仕組み:
//   - 自前のオーバーレイ div を textarea wrapper に absolute 重ね（z-index: 2.5、
//     Phase 1 の z-index 2 と Ghost の z-index 3 の間）
//   - input 停止後 800ms で LLM へ rewrite 提案リクエスト（重い処理なので長めの debounce）
//   - レスポンスを受けて青い波線スパンを描画
//   - クリック → blue-tooltip で候補選択 → 採用で textarea を置換
//   - dismissedKeys でセッション中の「このままにする」を記憶
//
// PoC 制約:
//   - 提案は options.fetcher で取得（注入式）。未指定なら何も起きない
//   - キャンセル: 新規入力時に in-flight をキャンセル

import { showBlueTooltip, hideBlueTooltip } from "./blue-tooltip";

const TRIGGER_DELAY_MS = 800;
const MIN_TEXT_LENGTH = 30; // この字数未満なら LLM 呼ばない（無駄打ち防止）

const state = new WeakMap();

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const STYLE_PROPS_TO_COPY = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "lineHeight",
  "letterSpacing",
  "textAlign",
  "textIndent",
  "whiteSpace",
  "wordBreak",
  "wordWrap",
  "overflowWrap",
  "tabSize",
];

function createBlueOverlay(wrapper) {
  const overlay = document.createElement("div");
  overlay.className = "proofread-blue-overlay";
  overlay.setAttribute("aria-hidden", "true");
  const cs = window.getComputedStyle(wrapper);
  if (cs.position === "static") {
    wrapper.style.position = "relative";
  }
  wrapper.appendChild(overlay);
  return overlay;
}

function syncOverlayStyle(overlay, textarea) {
  const cs = window.getComputedStyle(textarea);
  for (const prop of STYLE_PROPS_TO_COPY) {
    overlay.style[prop] = cs[prop];
  }
  overlay.style.paddingTop = cs.paddingTop;
  overlay.style.paddingRight = cs.paddingRight;
  overlay.style.paddingBottom = cs.paddingBottom;
  overlay.style.paddingLeft = cs.paddingLeft;
  overlay.style.borderTop = `${cs.borderTopWidth} solid transparent`;
  overlay.style.borderRight = `${cs.borderRightWidth} solid transparent`;
  overlay.style.borderBottom = `${cs.borderBottomWidth} solid transparent`;
  overlay.style.borderLeft = `${cs.borderLeftWidth} solid transparent`;
  overlay.style.boxSizing = cs.boxSizing;

  overlay.style.position = "absolute";
  overlay.style.top = `${textarea.offsetTop}px`;
  overlay.style.left = `${textarea.offsetLeft}px`;
  overlay.style.width = `${textarea.offsetWidth}px`;
  overlay.style.height = `${textarea.offsetHeight}px`;

  overlay.style.pointerEvents = "none";
  overlay.style.overflow = "hidden";
  overlay.style.color = "transparent";
  overlay.style.background = "transparent";
  // Phase 1 (z-index 2) より上、Ghost (z-index 3) より下
  overlay.style.zIndex = "2";
}

function destroyBlueOverlay(overlay) {
  if (overlay && overlay.parentNode) {
    overlay.parentNode.removeChild(overlay);
  }
}

/**
 * blue-wave を textarea に attach する。
 *
 * @param {HTMLTextAreaElement} textarea
 * @param {object} [options]
 * @param {(args: { text: string, context: string, signal?: AbortSignal }) => Promise<Array>} [options.fetcher]
 *   suggestions 配列を返す async 関数。各要素: { original, range, candidates, reason }
 * @param {string} [options.context]  対象欄ラベル
 * @returns {{ destroy: () => void }}
 */
export function attachBlueWave(textarea, options) {
  if (!textarea || state.has(textarea)) {
    return { destroy: () => {} };
  }
  const wrapper = textarea.parentNode;
  if (!wrapper) return { destroy: () => {} };

  const fetcher = options && typeof options.fetcher === "function" ? options.fetcher : null;
  const context = (options && options.context) || "文章";
  // 採用 / 破棄イベント通知（注入式・任意）
  const onEvent = (options && typeof options.onEvent === "function") ? options.onEvent : null;

  const overlay = createBlueOverlay(wrapper);
  syncOverlayStyle(overlay, textarea);

  const ctx = {
    overlay,
    suggestions: [],            // 最新の suggestion 一覧
    dismissedKeys: new Set(),   // 「このままにする」したキー（ruleType + original）
    triggerTimer: null,
    inflightController: null,
    handlers: {},
    resizeObserver: null,
  };

  function clearTriggerTimer() {
    if (ctx.triggerTimer) {
      clearTimeout(ctx.triggerTimer);
      ctx.triggerTimer = null;
    }
  }

  function abortInflight() {
    if (ctx.inflightController) {
      try {
        ctx.inflightController.abort();
      } catch (e) {
        /* noop */
      }
      ctx.inflightController = null;
    }
  }

  function makeDismissKey(suggestion) {
    return "blue:" + (suggestion.original || "");
  }

  function filteredSuggestions() {
    if (ctx.dismissedKeys.size === 0) return ctx.suggestions;
    return ctx.suggestions.filter((s) => !ctx.dismissedKeys.has(makeDismissKey(s)));
  }

  // suggestions を overlay に描画
  function renderMarks() {
    syncOverlayStyle(overlay, textarea);
    const text = textarea.value || "";
    const visible = filteredSuggestions();

    if (visible.length === 0) {
      overlay.innerHTML = escapeHtml(text) + "\u200b";
      overlay.scrollTop = textarea.scrollTop;
      overlay.scrollLeft = textarea.scrollLeft;
      return;
    }

    // range で sort + 重なり除去 + 位置の整合性チェック（stale フィルタ）
    const ranges = visible
      .filter((s) => Array.isArray(s.range) && s.range.length === 2)
      .map((s) => ({ start: s.range[0], end: s.range[1], suggestion: s }))
      .filter((r) => r.start >= 0 && r.end <= text.length && r.start < r.end)
      // 元のテキストが range 位置にまだ存在するもののみ表示（編集で位置ずれた古い mark を除外）
      .filter((r) => text.slice(r.start, r.end) === r.suggestion.original)
      .sort((a, b) => a.start - b.start || a.end - b.end);

    const merged = [];
    for (const r of ranges) {
      const last = merged[merged.length - 1];
      if (last && r.start < last.end) continue; // 重なり → スキップ
      merged.push(r);
    }

    const parts = [];
    let cursor = 0;
    for (let i = 0; i < merged.length; i++) {
      const r = merged[i];
      if (r.start > cursor) {
        parts.push(escapeHtml(text.slice(cursor, r.start)));
      }
      const markText = text.slice(r.start, r.end);
      parts.push(
        `<span class="proofread-blue-mark" data-blue-idx="${i}">${escapeHtml(markText)}</span>`
      );
      cursor = r.end;
    }
    if (cursor < text.length) {
      parts.push(escapeHtml(text.slice(cursor)));
    }
    parts.push("\u200b");

    overlay.innerHTML = parts.join("");
    overlay.scrollTop = textarea.scrollTop;
    overlay.scrollLeft = textarea.scrollLeft;

    // クリックハンドラ装着
    const markNodes = overlay.querySelectorAll(".proofread-blue-mark");
    markNodes.forEach((node) => {
      node.style.pointerEvents = "auto";
      const idx = parseInt(node.getAttribute("data-blue-idx"), 10);
      const r = merged[idx];
      if (!r) return;
      // モバイルで青波線タップ時のソフトキーボード維持のため、
      // mousedown で preventDefault して textarea の blur を抑止する。
      node.addEventListener("mousedown", (e) => {
        e.preventDefault();
      });
      node.addEventListener("click", (e) => {
        e.stopPropagation();
        showBlueTooltip(r.suggestion, node, textarea, {
          onApply: (candidate, candidateIndex) =>
            applySuggestion(r.suggestion, candidate, candidateIndex),
          onDismiss: () => {
            ctx.dismissedKeys.add(makeDismissKey(r.suggestion));
            // 「このままにする」= 明示破棄として記録
            if (onEvent) onEvent("dismiss");
            renderMarks();
          },
        });
      });
    });
  }

  function applySuggestion(suggestion, candidate, candidateIndex) {
    if (!Array.isArray(suggestion.range)) return;
    const [start, end] = suggestion.range;
    const text = textarea.value || "";
    const next = text.slice(0, start) + candidate + text.slice(end);

    // 採用前に、他の suggestion の range をずらす（後方の suggestion のみ shift）
    const lengthDiff = candidate.length - (end - start);
    ctx.suggestions = ctx.suggestions
      .filter((s) => s !== suggestion)
      .map((s) => {
        if (Array.isArray(s.range) && s.range[0] >= end) {
          return Object.assign({}, s, {
            range: [s.range[0] + lengthDiff, s.range[1] + lengthDiff],
          });
        }
        return s;
      });

    // applying フラグを立てて、続く onInput で suggestions が消されないようにする
    ctx.applying = true;
    const proto = Object.getPrototypeOf(textarea);
    const descriptor = Object.getOwnPropertyDescriptor(proto, "value");
    if (descriptor && descriptor.set) {
      descriptor.set.call(textarea, next);
    } else {
      textarea.value = next;
    }
    // v-model 同期
    textarea.dispatchEvent(new Event("input", { bubbles: true }));
    ctx.applying = false;

    // 採用イベント記録（どの候補が選ばれたかを candidate_index で残す）
    if (onEvent) onEvent("accept", { candidateIndex: candidateIndex });

    renderMarks();
    hideBlueTooltip();
  }

  function requestSuggestions() {
    clearTriggerTimer();
    abortInflight();
    if (!fetcher) return;
    const text = textarea.value || "";
    if (text.length < MIN_TEXT_LENGTH) {
      ctx.suggestions = [];
      renderMarks();
      return;
    }
    ctx.triggerTimer = setTimeout(async () => {
      if (!state.has(textarea)) return;
      const controller = (typeof AbortController !== "undefined") ? new AbortController() : null;
      ctx.inflightController = controller;
      const signal = controller ? controller.signal : null;
      try {
        const suggestions = await fetcher({
          text,
          context,
          signal,
        });
        if (ctx.inflightController !== controller) return;
        ctx.inflightController = null;
        if (!state.has(textarea)) return;
        if ((textarea.value || "") !== text) return;
        ctx.suggestions = Array.isArray(suggestions) ? suggestions : [];
        renderMarks();
      } catch (err) {
        if (signal && signal.aborted) return;
        // eslint-disable-next-line no-console
        console.warn("[blue-wave] fetch failed", err);
      }
    }, TRIGGER_DELAY_MS);
  }

  // ===== イベントハンドラ =====

  const onInput = () => {
    // 入力中も suggestions は保持する（基本消えない方針）。
    // - 位置がずれて original と一致しなくなった suggestion は renderMarks 内で自動フィルタされる
    // - applying=true のとき（採用ボタン経由の dispatch）は、新規 LLM リクエストを発射しない
    //   → 残った suggestion が新レスポンスで上書き・崩されないようにする
    if (ctx.applying) {
      // 採用適用時は再描画だけ行う（位置 shift は applySuggestion 側で完了済み）
      renderMarks();
      return;
    }
    hideBlueTooltip();
    renderMarks();
    requestSuggestions();
  };

  const onScroll = () => {
    overlay.scrollTop = textarea.scrollTop;
    overlay.scrollLeft = textarea.scrollLeft;
  };

  const onBlur = () => {
    // blur 時に suggestion 残ってればそのまま、消えていればそのまま
  };

  ctx.handlers = { onInput, onScroll, onBlur };
  textarea.addEventListener("input", onInput);
  textarea.addEventListener("scroll", onScroll);
  textarea.addEventListener("blur", onBlur);

  if (typeof ResizeObserver !== "undefined") {
    ctx.resizeObserver = new ResizeObserver(() => {
      renderMarks();
    });
    ctx.resizeObserver.observe(textarea);
  }

  state.set(textarea, ctx);

  // 初回: focus されている場合は提案を取りに行く
  if (textarea.offsetWidth > 0 && (textarea.value || "").length >= MIN_TEXT_LENGTH) {
    requestSuggestions();
  }

  function destroy() {
    clearTriggerTimer();
    abortInflight();
    try {
      textarea.removeEventListener("input", onInput);
      textarea.removeEventListener("scroll", onScroll);
      textarea.removeEventListener("blur", onBlur);
    } catch (e) {
      /* noop */
    }
    if (ctx.resizeObserver) {
      try {
        ctx.resizeObserver.disconnect();
      } catch (e) {
        /* noop */
      }
    }
    destroyBlueOverlay(overlay);
    hideBlueTooltip();
    state.delete(textarea);
  }

  return { destroy };
}
